import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { memo } from 'react';
import { twMerge } from 'tailwind-merge';

import LazyHydration from 'components/LazyHydration';

import {
  FEED_DESKTOP_ONLY_BLOCK,
  FEED_DESKTOP_ONLY_FLEX,
} from '../feedBreakpoints';
import { CARD_SURFACES } from '../surfaces';
import HomeOfferCardSkeleton from './Skeleton';

const Card = dynamic(() => import('./Card'));

/**
 * Starts hydrating feed cards well before they reach the viewport, so the
 * dynamic import of `./Card` has time to resolve and the skeleton is replaced
 * by the real card before the reader gets there. Scoped to the feed: it is the
 * only surface long enough to be scrolled through at speed.
 */
const FEED_ROOT_MARGIN = '1500px';

/**
 * Floor for the space the card occupies before it hydrates. Neither
 * pre-hydration branch of LazyHydration reserves height on its own: the empty
 * placeholder collapses to zero, and the skeleton has no floor either. Since
 * the flag that picks between them is not reactive, a collapsed card never
 * recovers until the observer hydrates it.
 *
 * A floor on `className` covers both branches, and `min-height` cannot clip a
 * card that runs taller than the measurement — a long title or an extra tag.
 *
 * Both numbers come from the rendered card in a production build, measured on
 * the surface that is actually visible at that breakpoint, since the surfaces
 * are mutually exclusive:
 *
 * - 341px — featured carousel slide at 412px wide, two-line title, no tag row
 * - 401px — featured grid cell at 1350px wide, same card content
 *
 * Re-measure the same way if the card gains a row: a stale floor silently
 * turns into either a residual gap or wasted space.
 */
const FEED_PLACEHOLDER_FLOOR = 'lg:min-h-[401px]';

const FEATURED_PLACEHOLDER_FLOOR = 'min-h-[341px] lg:min-h-[401px]';

/**
 * While not hydrated, a card renders the skeleton as the LazyHydration
 * `fallback` instead of an empty placeholder, so the space it occupies is
 * never blank. This covers every card mounted on the client with no server
 * markup to preserve: the infinite-scroll items, and any card on a
 * client-side navigation back to the home.
 *
 * The fallback is not restricted to a surface. Server-rendered instances keep
 * their markup regardless, because LazyHydration only swaps in the fallback
 * for instances mounted after the initial hydration — so there is no
 * hydration mismatch to avoid here.
 *
 * The carousel is the exception: `HomeOfferCardSkeleton` mirrors the desktop
 * non-carousel layout, so inside a slide it would flash the wrong shape and
 * then swap for a compact card. The slide keeps the height floor and goes
 * without the skeleton until there is one shaped for it.
 *
 * In the feed the mobile layout comes from MobileOfferCard, so this card is
 * desktop-only — both the card itself and the LazyHydration wrapper around it
 * (and therefore the skeleton) have to stay hidden below `lg`. That rule lives
 * here rather than at the call site, so the wrapper and the card can never
 * disagree about which breakpoints they appear on. The featured carousel and
 * grid render this card at every breakpoint and handle their own visibility.
 */
const HomeOfferCard = ({
  className,
  isCarousel = false,
  surface,
  ...props
}) => {
  const isFeed = surface === 'feed';

  return (
    <LazyHydration
      className={twMerge(
        isFeed && FEED_DESKTOP_ONLY_BLOCK,
        isFeed ? FEED_PLACEHOLDER_FLOOR : FEATURED_PLACEHOLDER_FLOOR
      )}
      fallback={isCarousel ? undefined : <HomeOfferCardSkeleton />}
      rootMargin={isFeed ? FEED_ROOT_MARGIN : undefined}
    >
      <Card
        className={twMerge(isFeed && FEED_DESKTOP_ONLY_FLEX, className)}
        isCarousel={isCarousel}
        surface={surface}
        {...props}
      />
    </LazyHydration>
  );
};

HomeOfferCard.propTypes = {
  authors: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      username: PropTypes.string.isRequired,
      photo: PropTypes.string.isRequired,
      typeName: PropTypes.string.isRequired,
      specialityName: PropTypes.string,
      level: PropTypes.number.isRequired,
      link: PropTypes.string.isRequired,
    })
  ),
  cardTitleAs: PropTypes.string,
  className: PropTypes.string,
  isCarousel: PropTypes.bool,
  gaEventCategory: PropTypes.string.isRequired,
  offerComments: PropTypes.number.isRequired,
  offerIsHighlight: PropTypes.bool.isRequired,
  offerImagePriority: PropTypes.bool,
  offerOldPrice: PropTypes.number,
  offerPhoto: PropTypes.string.isRequired,
  offerPrice: PropTypes.number.isRequired,
  offerPriceType: PropTypes.string.isRequired,
  offerPublished: PropTypes.string.isRequired,
  offerSlug: PropTypes.string.isRequired,
  offerStatusName: PropTypes.string.isRequired,
  offerUserVisibility: PropTypes.string.isRequired,
  offerTags: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    })
  ),
  offerTitle: PropTypes.string.isRequired,
  storeDomain: PropTypes.string.isRequired,
  surface: PropTypes.oneOf(CARD_SURFACES),
  userName: PropTypes.string.isRequired,
  userPhoto: PropTypes.string.isRequired,
  userUsername: PropTypes.string.isRequired,
  onImageClick: PropTypes.func,
  onTitleClick: PropTypes.func,
};

export default memo(HomeOfferCard);
